export type SoundEventId =
  | 'drawSlide'
  | 'playFlick'
  | 'tableThud'
  | 'shuffleRiffle'
  | 'tapClick'
  | 'hoverTick'
  | 'lifeTick'
  | 'flagChime';

export interface SoundPreferences {
  muted: boolean;
  masterVolume: number;
  volumes: Record<SoundEventId, number>;
}

const STORAGE_KEY = 'playtable_sound_prefs_v1';

const DEFAULT_PREFS: SoundPreferences = {
  muted: false,
  masterVolume: 0.75,
  volumes: {
    drawSlide: 0.85,
    playFlick: 0.85,
    tableThud: 0.9,
    shuffleRiffle: 0.8,
    tapClick: 0.75,
    hoverTick: 0.35,
    lifeTick: 0.65,
    flagChime: 0.95,
  },
};

class WebAudioTabletopSynth {
  private ctx: AudioContext | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private prefs: SoundPreferences;

  constructor() {
    this.prefs = this.loadPrefs();
  }

  private loadPrefs(): SoundPreferences {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return structuredClone(DEFAULT_PREFS);
      const parsed = JSON.parse(raw);
      return {
        muted: Boolean(parsed.muted),
        masterVolume: typeof parsed.masterVolume === 'number' ? parsed.masterVolume : 0.75,
        volumes: { ...DEFAULT_PREFS.volumes, ...(parsed.volumes ?? {}) },
      };
    } catch {
      return structuredClone(DEFAULT_PREFS);
    }
  }

  getPrefs(): SoundPreferences {
    return structuredClone(this.prefs);
  }

  updatePrefs(patch: Partial<SoundPreferences> & { eventVolumes?: Partial<Record<SoundEventId, number>> }): SoundPreferences {
    if (patch.muted !== undefined) this.prefs.muted = patch.muted;
    if (patch.masterVolume !== undefined) this.prefs.masterVolume = Math.max(0, Math.min(1, patch.masterVolume));
    if (patch.eventVolumes) {
      for (const [k, v] of Object.entries(patch.eventVolumes)) {
        if (typeof v === 'number') {
          this.prefs.volumes[k as SoundEventId] = Math.max(0, Math.min(1, v));
        }
      }
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.prefs));
    } catch {
      // ignore storage errors
    }
    return this.getPrefs();
  }

  private ensureContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    if (!this.ctx) {
      this.ctx = new Ctor();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
    if (!this.noiseBuffer && this.ctx) {
      const sampleRate = this.ctx.sampleRate;
      const length = Math.floor(sampleRate * 0.4);
      const buffer = this.ctx.createBuffer(1, length, sampleRate);
      const data = buffer.getChannelData(0);
      let b0 = 0;
      let b1 = 0;
      let b2 = 0;
      for (let i = 0; i < length; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99765 * b0 + white * 0.099046;
        b1 = 0.963 * b1 + white * 0.2965164;
        b2 = 0.57 * b2 + white * 1.0526913;
        data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.18;
      }
      this.noiseBuffer = buffer;
    }
    return this.ctx;
  }

  play(event: SoundEventId): void {
    if (this.prefs.muted) return;
    const gainScale = this.prefs.masterVolume * (this.prefs.volumes[event] ?? 0.7);
    if (gainScale <= 0.01) return;

    const ctx = this.ensureContext();
    if (!ctx || !this.noiseBuffer) return;

    const now = ctx.currentTime;

    switch (event) {
      case 'drawSlide': {
        // Card sleeve sliding briskly off top of library
        const src = ctx.createBufferSource();
        src.buffer = this.noiseBuffer;
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.setValueAtTime(950, now);
        filter.frequency.exponentialRampToValueAtTime(2600, now + 0.11);
        filter.Q.value = 1.8;

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.001, now);
        gain.gain.linearRampToValueAtTime(0.28 * gainScale, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

        src.connect(filter).connect(gain).connect(ctx.destination);
        src.start(now);
        src.stop(now + 0.13);
        break;
      }

      case 'playFlick': {
        // Crisp cardstock flick
        const src = ctx.createBufferSource();
        src.buffer = this.noiseBuffer;
        const hp = ctx.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 2100;

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.36 * gainScale, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.045);

        src.connect(hp).connect(gain).connect(ctx.destination);
        src.start(now);
        src.stop(now + 0.05);
        break;
      }

      case 'tableThud': {
        // Warm neoprene playmat thud + card settle
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(142, now);
        osc.frequency.exponentialRampToValueAtTime(46, now + 0.11);

        const oscGain = ctx.createGain();
        oscGain.gain.setValueAtTime(0.34 * gainScale, now);
        oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

        osc.connect(oscGain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.13);

        // Subtle snap transient on top of thud
        const src = ctx.createBufferSource();
        src.buffer = this.noiseBuffer;
        const lp = ctx.createBiquadFilter();
        lp.type = 'bandpass';
        lp.frequency.value = 1500;
        const nGain = ctx.createGain();
        nGain.gain.setValueAtTime(0.18 * gainScale, now);
        nGain.gain.exponentialRampToValueAtTime(0.001, now + 0.035);
        src.connect(lp).connect(nGain).connect(ctx.destination);
        src.start(now);
        src.stop(now + 0.04);
        break;
      }

      case 'shuffleRiffle': {
        // 9 staggered sleeve riffle micro-snaps
        for (let i = 0; i < 9; i++) {
          const t = now + i * 0.022 + Math.random() * 0.004;
          const src = ctx.createBufferSource();
          src.buffer = this.noiseBuffer;
          const bp = ctx.createBiquadFilter();
          bp.type = 'bandpass';
          bp.frequency.value = 1400 + i * 160;
          bp.Q.value = 2.4;

          const g = ctx.createGain();
          g.gain.setValueAtTime(0.22 * gainScale, t);
          g.gain.exponentialRampToValueAtTime(0.001, t + 0.024);

          src.connect(bp).connect(g).connect(ctx.destination);
          src.start(t);
          src.stop(t + 0.028);
        }
        break;
      }

      case 'tapClick': {
        const src = ctx.createBufferSource();
        src.buffer = this.noiseBuffer;
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.setValueAtTime(1250, now);
        bp.frequency.linearRampToValueAtTime(850, now + 0.05);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.2 * gainScale, now);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.055);
        src.connect(bp).connect(g).connect(ctx.destination);
        src.start(now);
        src.stop(now + 0.06);
        break;
      }

      case 'hoverTick': {
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(1750, now);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.06 * gainScale, now);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.014);
        osc.connect(g).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.016);
        break;
      }

      case 'lifeTick': {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(520, now);
        osc.frequency.exponentialRampToValueAtTime(680, now + 0.035);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.12 * gainScale, now);
        g.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
        osc.connect(g).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.045);
        break;
      }

      case 'flagChime': {
        const notes = [587.33, 880]; // D5 -> A5 social alert
        notes.forEach((freq, idx) => {
          const start = now + idx * 0.085;
          const osc = ctx.createOscillator();
          osc.type = 'triangle';
          osc.frequency.setValueAtTime(freq, start);
          const g = ctx.createGain();
          g.gain.setValueAtTime(0.24 * gainScale, start);
          g.gain.exponentialRampToValueAtTime(0.001, start + 0.18);
          osc.connect(g).connect(ctx.destination);
          osc.start(start);
          osc.stop(start + 0.19);
        });
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
          navigator.vibrate(60);
        }
        break;
      }
    }
  }
}

export const soundEngine = new WebAudioTabletopSynth();
